import { TextFieldProps } from '@mui/material'
import { ChangeEvent } from 'react'
import { Controller, ControllerProps, useFormContext } from 'react-hook-form'
import { TextField } from 'ui'

export type DynamicFormInputProps = {
  name: ControllerProps['name']
  inputProps: TextFieldProps & { 'data-testid'?: string }
  className?: string
  numericOnly?: boolean // Разрешить только цифры, но сохранять как строку
  allowAllChars?: boolean // Разрешить любые символы (для названий организаций и т.д.)
  rules?: ControllerProps['rules']
}

// компонент react-hook-form с текстовым полем
export function FormInputText({
  name,
  inputProps,
  className,
  numericOnly = false,
  allowAllChars = false,
  rules,
}: DynamicFormInputProps) {
  const { control } = useFormContext()
  const { 'data-testid': dataTestId, ...restInputProps } = inputProps

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          const rawValue = e.target.value
          const isEmailInput = restInputProps?.type === 'email'
          const isNumberInput = restInputProps?.type === 'number'

          let targetValue: string | number = rawValue

          if (isNumberInput) {
            // Для числовых полей разрешаем только цифры
            targetValue = rawValue.replace(/\D/g, '')
            // Конвертируем в number, если есть значение
            field.onChange(targetValue ? Number(targetValue) : undefined)
            return
          }

          if (numericOnly) {
            // Для полей "только цифры" (ИНН, КПП, БИК и т.д.) - фильтруем все кроме цифр, но сохраняем как строку
            targetValue = rawValue.replace(/\D/g, '')
            field.onChange(targetValue)
            return
          }

          if (!isEmailInput && !allowAllChars) {
            // Для текстовых полей фильтруем символы (только буквы, пробелы, дефис)
            targetValue = rawValue.replace(/[^\sa-zA-Zа-яА-ЯёЁ-]/g, '')
          }

          field.onChange(targetValue)
        }
        const helperText =
          fieldState?.error?.message ?? restInputProps?.helperText
        return (
          <TextField
            className={className}
            inputProps={{
              autoComplete: 'off',
              ...(numericOnly && { inputMode: 'numeric' as const }),
              ...(dataTestId && { 'data-testid': dataTestId }),
            }}
            {...restInputProps}
            {...field}
            value={field.value ?? ''}
            error={!!fieldState?.error}
            helperText={helperText}
            onChange={handleChange}
          />
        )
      }}
    />
  )
}
